import { Switch } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { isRoleDenied } from "../../../utils/permissions";

interface TableProps {
  columns: string[];
  columnHeaders: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder: React.Dispatch<
    React.SetStateAction<{ id: number; status: string } | null>
  >;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  onOemClick?: (oemName: string, commonOemList: any) => void;
}


const ProductTableComponent: React.FC<TableProps> = ({
  columns,
  columnHeaders,
  data: initialData,
  setSelectedOrder,
  currentPage,
  setCurrentPage,
  totalPages,
  onOemClick,
}) => {
  const [tableData, setTableData] = useState(initialData);
  const navigate = useNavigate();

  const canToggleStatus = !isRoleDenied(["ROLE_ERP_FRANCHISE_ADMIN", "ROLE_FRANCHISE_INVENTORY_MANAGER"]);
  const canViewOem = !isRoleDenied(["ROLE_ERP_FRANCHISE_ADMIN", "ROLE_FRANCHISE_INVENTORY_MANAGER"]);
  const visibleColumns = canViewOem
    ? columns
    : columns.filter((column) => column !== "oemName");
  const visibleColumnHeaders = canViewOem
    ? columnHeaders
    : columnHeaders.filter((header) => header !== "OEM Type");

  React.useEffect(() => {
    setTableData(initialData);
  }, [initialData]);

  const handleStatusChange = async (id: number, currentStatus: number) => {
    const newStatus = currentStatus === 1 ? 0 : 1;

    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, status: newStatus } : row
      )
    );

    try {
      await AllPartApi.api.changePartStatus(
        id.toString(),
        newStatus.toString()
      );
    } catch (error) {
      console.error(`Failed to change status for item ${id}`, error);

      setTableData((prevData) =>
        prevData.map((row) =>
          row.id === id ? { ...row, status: currentStatus } : row
        )
      );
    }
  };

  const handleViewClick = (row: Record<string, any>) => {
    navigate(`/parts/${row.id}`, {
      state: { isSpecialPart: row.isSpecialPart },
    });
  };

  // Calculate the correct row number based on items per page (10)
  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  return (
    <div className="w-full">
      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 w-16 min-w-16 max-w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              {visibleColumnHeaders.map((header, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                    header === "Product ID"
                      ? "sticky left-16 z-10 w-24 min-w-24 max-w-24 bg-gray-50"
                      : header === "Product"
                        ? "min-w-[180px] text-center"
                      : ""
                  }`}>
                  {header}
                </th>
              ))}
              <th className="sticky right-0 z-10 bg-gray-50 w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => {
                const rowNumber = getRowNumber(rowIndex);

                return (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="sticky left-0 z-10 bg-white px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {visibleColumns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-4 py-4 text-sm text-gray-900 whitespace-nowrap ${
                          column === "id"
                            ? "sticky left-16 z-10 w-24 min-w-24 max-w-24 bg-white"
                            : column === "description"
                              ? "min-w-[180px]"
                              : ""
                        }`}>
                        {column === "status" ? (
                          <div className="flex items-center">
                            {canToggleStatus && (
                              <Switch
                                checked={row.status === 1}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(row.id, row.status);
                                }}
                                color="primary"
                                size="small"
                                inputProps={{ "aria-label": "status toggle" }}
                              />
                            )}
                            <span
                              className={`${canToggleStatus ? "ml-2" : ""} inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                row.status === 1
                                  ? "bg-green-100 text-green-800 border border-green-300"
                                  : "bg-gray-100 text-gray-800 border border-gray-300"
                              }`}>
                              {row.status === 1 ? "ON" : "OFF"}
                            </span>
                          </div>
                        ) : column === "procurable" ||
                          column === "isSpecialPart" ? (
                          <span>
                            {row[column] === true
                              ? "True"
                              : row[column] === false
                                ? "False"
                                : "N/A"}
                          </span>
                        ) : column === "salesPrice" ||
                          column === "costPrice" ? (
                          <span>
                            ₹
                            {row[column] !== undefined
                              ? Number(row[column]).toFixed(2)
                              : "0.00"}
                          </span>
                        ) : column === "oemName" ? (
                          Array.isArray(row.commonOem) &&
                          row.commonOem.length > 0 ? (
                            <button
                              onClick={() =>
                                onOemClick?.(
                                  row.oemName || "OEM",
                                  row.commonOem
                                )
                              }
                              className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-200 transition-colors cursor-pointer">
                              View List ({row.commonOem.length})
                            </button>
                          ) : (
                            <span>
                              {row[column] !== undefined
                                ? row[column]?.toString()
                                : "N/A"}
                            </span>
                          )
                        ) : column === "partFamily" ? (
                          <span>{row.partFamily?.partFamily ?? "N/A"}</span>
                        ) : column === "partLocation" ? (
                          <span>{row.partFamily?.partLocation ?? "N/A"}</span>
                        ) : (
                          <span>
                            {row[column] !== undefined
                              ? row[column]?.toString()
                              : "N/A"}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="sticky right-0 z-10 bg-white px-4 py-4">
                      <button
                        onClick={() => handleViewClick(row)}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={visibleColumnHeaders.length + 2}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No products found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTableComponent;